/**
 * Public site data that belongs to whoever runs the game, not to whoever wrote
 * it: the company, its social links, the enabled payment methods and the legal
 * documents.
 *
 * All admin-managed — `GET /api/site-info`, `GET /api/legal` and
 * `GET /api/cms/*`, all public — so none of it is hard-coded here.
 *
 * ## Deliberately not `net/api.ts`
 *
 * That module's `request()` treats a 401 as "refresh the session and retry".
 * These are public reads made by a visitor who has no session at all, so a
 * server that answered 401 would send the page off to `POST /api/auth/refresh`
 * for nothing, on the one screen where the visitor is provably not signed in.
 * Plain `fetch`, and every failure resolves to nothing rather than rejecting —
 * the footer hides what it has no data for. A CMS that is down must not be able
 * to stop sign-in being reachable.
 */
import type { LegalDoc as LegalListEntry } from '../state/legal-slugs';

const BASE = import.meta.env.VITE_API_URL || '';

export interface CompanyInfo {
  name: string;
  email: string;
  address: string;
  registration: string;
  gameName: string;
}

export interface SocialLink {
  platform: string;
  url: string;
}

/**
 * One enabled payment method, as `/api/site-info` sends it — `slug` matches
 * the icon filename (`/payment-methods/{icon}.svg` ships all 88 of them as
 * static assets; see `payment-methods.constants.ts` in core), `name` is the
 * accessible label. Only *enabled* methods are ever sent — the admin panel's
 * toggle list is what turns one on, and every entry starts disabled.
 */
export interface PaymentMethodInfo {
  slug: string;
  name: string;
  icon: string;
}

export interface LegalDoc {
  slug: string;
  title: string;
  /**
   * Admin-authored HTML, authored through Quill in the admin panel. Rendered
   * with `v-html="sanitizeHtml(doc.content)"` — see `LegalOverlay.vue` — never
   * trusted raw, because it reaches every visitor's browser regardless of who
   * wrote it.
   */
  content: string;
}

async function getJson<T>(path: string): Promise<T | null> {
  try {
    const response = await fetch(`${BASE}${path}`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    return (await response.json()) as T;
  } catch {
    return null;
  }
}

/**
 * What core's `/api/site-info` answers for a blank `company.game_name`
 * (`site-info.controller.ts`). It is a placeholder, not a name: the footer
 * shows its own credit line instead of printing it.
 */
const CORE_GAME_NAME_FALLBACK = 'Game';

function gameNameOf(raw: string | undefined): string {
  const name = (raw ?? '').trim();
  return name === CORE_GAME_NAME_FALLBACK ? '' : name;
}

interface SiteInfoResponse {
  company?: Partial<CompanyInfo> & { registration?: string; game_name?: string };
  paymentMethods?: { slug?: string; name?: string; icon?: string }[];
}

/**
 * Cached at module scope, not component scope — the same discipline
 * `cachedShowcaseSpecs` below applies, and for the identical reason:
 * `LandingScreen` (and therefore its child `SiteFooter`) remounts every time
 * a visitor bounces from the sign-in form back to `#top`, and the company
 * block and payment row cannot have changed in between, so a second mount
 * reusing the first mount's answer is a cache hit, not a stale one. A
 * *failed* fetch is deliberately not cached, so a transient miss stays
 * retryable the next time the page is looked at.
 */
let cachedSiteInfo: Promise<{
  company: CompanyInfo | null;
  paymentMethods: PaymentMethodInfo[];
}> | null = null;

/**
 * The footer's company block and payment row in one request.
 *
 * Social links are not here: core's `/api/site-info` reads `social.*` settings
 * while the admin writes the `SocialLink` table, so its `socialLinks` is always
 * empty — `fetchSocialLinks` reads the table's public route. Empty strings are normalised to absent
 * here so every consumer can ask one question (`is there a name`) rather than
 * two. `paymentMethods` is already filtered to enabled-only and sorted by the
 * server (`SiteInfoController`) — nothing here re-derives that.
 */
export function fetchSiteInfo(): Promise<{
  company: CompanyInfo | null;
  paymentMethods: PaymentMethodInfo[];
}> {
  if (cachedSiteInfo) return cachedSiteInfo;
  cachedSiteInfo = (async () => {
    const data = await getJson<SiteInfoResponse>('/api/site-info');
    if (!data) {
      cachedSiteInfo = null;
      return { company: null, paymentMethods: [] };
    }

    const c = data.company ?? {};
    const name = (c.name ?? '').trim();
    const company: CompanyInfo | null = name
      ? {
          name,
          email: (c.email ?? '').trim(),
          address: (c.address ?? '').trim(),
          registration: (c.registration ?? '').trim(),
          gameName: gameNameOf(c.gameName),
        }
      : null;

    const paymentMethods = (data.paymentMethods ?? [])
      .map((m) => ({
        slug: (m.slug ?? '').trim(),
        name: (m.name ?? '').trim(),
        icon: (m.icon ?? '').trim(),
      }))
      .filter((m) => m.slug.length > 0);

    return { company, paymentMethods };
  })();
  return cachedSiteInfo;
}

/**
 * The active social links in the admin's order — `GET /api/cms/social-links`
 * already filters to active rows and sorts by `sortOrder`. A link without an
 * http(s) URL is dropped; a down CMS resolves to an empty row.
 */
export async function fetchSocialLinks(): Promise<SocialLink[]> {
  const data = await getJson<Array<{ platform?: string; url?: string }>>('/api/cms/social-links');
  if (!Array.isArray(data)) return [];
  return data
    .map((l) => ({ platform: (l.platform ?? '').trim(), url: (l.url ?? '').trim() }))
    .filter((l) => /^https?:\/\//i.test(l.url));
}

/** One legal document, or `null` when the admin has not published it. */
export function fetchLegalDoc(slug: string): Promise<LegalDoc | null> {
  return getJson<LegalDoc>(`/api/cms/legal/${encodeURIComponent(slug)}`);
}

/**
 * Every published legal document, in the order the server lists them —
 * `GET /api/legal`, this game's own public read (see `ADMIN_API_CHANGELOG.md`'s
 * 2026-09-03 entry). Replaces the old hardcoded three-slug list: an operator
 * can add a document from the admin panel's Legal section and it appears here
 * without a deploy, the same "everything visible degrades" rule as the rest of
 * this file — a down or empty CMS resolves to an empty list, not an error.
 */
export async function fetchLegalList(): Promise<LegalListEntry[]> {
  const data = await getJson<Array<{ slug?: string; title?: string }>>('/api/legal');
  if (!Array.isArray(data)) return [];
  return data
    .map((d) => ({ slug: (d.slug ?? '').trim(), title: (d.title ?? '').trim() }))
    .filter((d) => d.slug.length > 0);
}

/**
 * A news post's shared fields — everything a preview card needs: thumbnail,
 * title, introduction and a display date. `createdAt` rides along
 * unrendered so `newsDisplayDate` has something to fall back to for a post
 * published immediately, with no explicit date set.
 */
